import React, { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { GoogleButton } from "@/components/GoogleButton";
import { ArrowRight, Gift } from "lucide-react";

export default function SignUp() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const loc = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [ref, setRef] = useState("");
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const r = new URLSearchParams(loc.search).get("ref");
    if (r) setRef(r.toUpperCase());
  }, [loc.search]);

  const submit = async (e) => {
    e.preventDefault();
    setErr(null);
    setBusy(true);
    try {
      await register({
        email: email.trim(),
        password,
        name: name.trim(),
        referral_code: ref.trim().toUpperCase() || null,
      });
      navigate("/");
    } catch (e) {
      const d = e.response?.data?.detail;
      setErr(typeof d === "string" ? d : "Could not create account");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-10 relative">
      <div className="w-full max-w-md relative z-[2]">
        <div className="text-center mb-8">
          <div className="kpi-label">Start free</div>
          <h1 className="display text-4xl mt-2 font-bold uppercase tracking-tight leading-none">
            Command<span className="text-oud">·</span>Center
          </h1>
          <div className="hairline-oud w-24 mx-auto mt-4" />
          <div className="serif-mark text-sm text-muted-foreground mt-3 tracking-wide">
            For CEO's Only
          </div>
          <div className="mono text-[11px] text-muted-foreground tracking-widest mt-3">
            $9/MO · NO CARD UNTIL TRIAL ENDS
          </div>
        </div>

        <form onSubmit={submit} className="hud-frame p-8 space-y-5" data-testid="signup-form">
          <div className="tread-plate h-1.5 -mx-8 -mt-8 mb-4" />

          {ref && (
            <div className="hud-chip hud-chip-on flex items-center gap-2 !text-[10px]" data-testid="signup-ref-banner">
              <Gift size={11} /> Referred — first month 50% off
            </div>
          )}

          <div>
            <div className="kpi-label">Name (optional)</div>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="hud-input mt-2"
              placeholder="Operator name"
              data-testid="signup-name"
            />
          </div>
          <div>
            <div className="kpi-label">Email</div>
            <input
              type="email"
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="hud-input mt-2"
              required
              data-testid="signup-email"
            />
          </div>
          <div>
            <div className="kpi-label">Password (6+ chars)</div>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="hud-input mt-2"
              minLength={6}
              required
              data-testid="signup-password"
            />
          </div>
          <div>
            <div className="kpi-label">Referral Code (optional)</div>
            <input
              value={ref}
              onChange={(e) => setRef(e.target.value.toUpperCase())}
              className="hud-input mt-2 mono uppercase tracking-widest"
              placeholder="ABCD1234"
              maxLength={12}
              data-testid="signup-ref"
            />
          </div>
          {err && (
            <div className="text-[hsl(var(--oxblood))] mono text-xs uppercase tracking-widest" data-testid="signup-error">
              {err}
            </div>
          )}
          <button
            type="submit"
            disabled={busy}
            className="hud-btn hud-btn-primary w-full justify-center"
            data-testid="signup-submit"
          >
            {busy ? "Creating account…" : "CREATE FREE ACCOUNT"} <ArrowRight size={11} />
          </button>

          <div className="flex items-center gap-3 my-1">
            <div className="flex-1 h-px bg-border" />
            <span className="mono text-[9px] uppercase tracking-[0.32em] text-muted-foreground">OR</span>
            <div className="flex-1 h-px bg-border" />
          </div>

          <GoogleButton referralCode={ref || null} next="/" label="Sign up with Google" />
        </form>

        <div className="mt-5 text-center mono text-[11px] uppercase tracking-widest text-muted-foreground">
          Already enrolled?{" "}
          <Link to="/signin" className="text-foreground hover:text-hud" data-testid="signup-to-signin">
            SIGN IN
          </Link>
        </div>
      </div>
    </div>
  );
}
